import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  toLocaleString,
  showTimeDiffPartial,
  formatNumber,
  N,
  shorten,
  aOrAn,
} from "@/lib/utils";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Separator } from "@/components/ui/separator";
import BenchIcon from "@/assets/bench.svg?react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Clock,
  Coins,
  ListChecks,
  Package,
  User,
  UserRound,
  WrenchIcon,
  AlertTriangle,
} from "lucide-react";
import { useMemo, useState } from "react";
import { AccountNoLink } from "@/components/links";
import {
  clearBenchRegistryCache,
  useBenchRegistry,
} from "./use-bench-registry";
import { useAlgoAcctInfo } from "@/hooks/use-algo-account";
import { useWallet } from "@txnlab/use-wallet";
import { ConnectButton } from "@/components/connect-button";
import { useAbelAssets } from "@/hooks/use-abel-assets";
import { AssetTinyLabels } from "abel-sdk";
import { decodeAddress, decodeUint64 } from "algosdk";
import {
  TransactionDialog,
  transactionInner,
} from "@/components/transaction/transaction-dialog";
import { makeCreateBenchTransactions } from "@/lib/clients";
import { benchRegistryAppId, defaultAbelAppId } from "@/config";
import { clearAllBenchCache } from "./use-all-bench-states";
import { WithErrorAndLoading } from "@/components/with-error-and-loading";
import { TermsAndConditionsField } from "./components/terms-and-conditions-field";
import { BlockedCountriesField } from "./components/blocked-countries-field";
import { countries as countryCodes } from "country-code-lookup";
import BenchHero from "./components/bench-hero";

function SelectAssetItem({
  asset,
  disabled,
}: {
  asset: AssetLabelWithAmount;
  disabled?: true;
}) {
  return (
    <SelectItem {...{ disabled }} key={asset.id} value={asset.id.toString()}>
      {asset.name}{" "}
      {asset.id ? (
        <span className="text-muted-foreground">({N(asset.id)})</span>
      ) : null}{" "}
      <div className="text-muted-foreground">
        Balance: {formatNumber(asset.amount, N(asset.decimals))}
      </div>
    </SelectItem>
  );
}

const unknownAsset = {
  id: -1,
  name: "Unknown",
  unitName: "Unknown",
  decimals: 6,
};

// Convert country codes to the format we need for geo-blocking (for review section)
const countries = countryCodes
  .map((country: any) => ({
    code: country.iso2,
    name: country.country,
  }))
  .filter((country: any) => country.code && country.name);

// Type for country objects
type CountryOption = {
  code: string;
  name: string;
};

// Zod schema for Bench creation form
const benchCreateSchema = z
  .object({
    sellingAssetId: z.string().min(1, "Asset for sale is required"),
    purchaseAssetId: z.string().min(1, "Asset for purchase is required"),
    amountForSale: z
      .string()
      .min(1, "Amount for sale is required")
      .refine((val) => !isNaN(Number(val)) && Number(val) > 0, {
        message: "Amount must be a positive number",
      }),
    assetPrice: z
      .string()
      .min(1, "Asset price is required")
      .refine((val) => !isNaN(Number(val)) && Number(val) > 0, {
        message: "Asset price must be a positive number",
      }),
    timeStart: z
      .string()
      .optional()
      .refine(
        (val) => {
          if (!val) return true;
          const timestamp = new Date(val).getTime();
          const now = Date.now();
          return timestamp > now;
        },
        {
          message: "Start time must be in the future",
        }
      ),
    timeEnd: z
      .string()
      .optional()
      .refine(
        (val) => {
          if (!val) return true;
          const timestamp = new Date(val).getTime();
          const now = Date.now();
          return timestamp > now;
        },
        {
          message: "End time must be in the future",
        }
      ),
    beneficiaryAddress: z
      .string()
      .optional()
      .refine(
        (val) => {
          if (!val) return true;
          try {
            decodeAddress(val);
            return true;
          } catch (e) {
            return false;
          }
        },
        {
          message: "Invalid Algorand address",
        }
      ),
    purchaseLimitPerAccount: z
      .string()
      .optional()
      .refine(
        (val) => {
          if (!val) return true;
          return !isNaN(Number(val)) && Number(val) > 0;
        },
        {
          message: "Purchase limit must be a positive number",
        }
      ),
    termsAndConditions: z
      .string()
      .optional()
      .refine(
        (val) => {
          if (!val) return true;
          try {
            new URL(val);
            return true;
          } catch (e) {
            return false;
          }
        },
        {
          message: "Please enter a valid URL",
        }
      ),
    blockedCountries: z.array(z.string()).optional().default([]),
  })
  .refine(
    (data) => {
      if (data.timeStart && data.timeEnd) {
        const startTime = new Date(data.timeStart).getTime();
        const endTime = new Date(data.timeEnd).getTime();
        return endTime > startTime;
      }
      return true;
    },
    {
      message: "End time must be after start time",
      path: ["timeEnd"],
    }
  )
  .refine(
    (data) => {
      if (data.sellingAssetId && data.purchaseAssetId) {
        return data.sellingAssetId !== data.purchaseAssetId;
      }
      return true;
    },
    {
      message: "Selling asset and purchase asset must be different",
      path: ["purchaseAssetId"],
    }
  );

type BenchCreateFormData = z.infer<typeof benchCreateSchema>;

interface AssetLabelWithAmount extends AssetTinyLabels {
  amount: bigint;
}

function useUserClassifiedAssets() {
  const {
    data: benchRegistryState,
    isPending: benchLoading,
    error: benchError,
  } = useBenchRegistry();
  const { activeAddress } = useWallet();
  const {
    data: accountData,
    isPending: accountLoading,
    error: accountError,
  } = useAlgoAcctInfo(activeAddress);

  const loading = benchLoading || accountLoading;
  const error = benchError || accountError;

  const userAssetIDs = useMemo(() => {
    if (accountData?.assets)
      return accountData.assets.map(({ assetId }) => BigInt(assetId));
  }, [accountData?.assets]);

  const { data: abelAssets } = useAbelAssets(
    activeAddress ? (benchRegistryState?.abelAppId || defaultAbelAppId) : 0n,
    userAssetIDs
  );

  const assets = useMemo(() => {
    const allowed: AssetLabelWithAmount[] = [
      {
        name: "ALGO",
        decimals: 6,
        amount: BigInt(accountData?.amount ?? 0),
        id: 0n,
        labels: [],
        unitName: "ALGO",
      },
    ];
    const banned: AssetLabelWithAmount[] = [];
    const unknown: AssetLabelWithAmount[] = [];
    if (!abelAssets) return { allowed, banned, unknown };

    for (const abelAsset of abelAssets.values()) {
      // @ts-ignore
      if (abelAsset.deleted) continue

      const amount = BigInt(
        accountData?.assets?.find((a) => BigInt(a.assetId) === abelAsset.id)
          ?.amount ?? 0n
      );

      const banLabel = abelAsset.labels.find((label) =>
        benchRegistryState?.banlistLabels.includes(label)
      );
      if (banLabel) {
        banned.push({ ...abelAsset, amount });
        continue;
      }
      // empty AL means allow all
      const allowLabel = benchRegistryState?.allowlistLabels.length
        ? abelAsset.labels.find((label) =>
            benchRegistryState?.allowlistLabels.includes(label)
          )
        : "implicit";
      if (allowLabel) {
        allowed.push({ ...abelAsset, amount });
        continue;
      }
      unknown.push({ ...abelAsset, amount });
    }
    return { allowed, banned, unknown };
  }, [
    abelAssets,
    benchRegistryState?.allowlistLabels,
    benchRegistryState?.banlistLabels,
  ]);

  return { loading, data: assets, error, };
}

export function BenchCreate() {
  const navigate = useNavigate();
  const { activeAddress } = useWallet();
  const {
    data: benchRegistryState,
    isPending: registryLoading,
    error: registryError,
  } = useBenchRegistry();
  const {
    loading: benchLoading,
    error: benchError,
    data: assets,
  } = useUserClassifiedAssets();

  const loading = registryLoading || benchLoading;
  const error = registryError ?? benchError;

  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState<"form" | "review">("form");
  const [acknowledgeWarning, setAcknowledgeWarning] = useState(false);

  const form = useForm<BenchCreateFormData>({
    resolver: zodResolver(benchCreateSchema),
    defaultValues: {
      sellingAssetId: "",
      purchaseAssetId: "",
      amountForSale: "",
      assetPrice: "",
      timeStart: "",
      timeEnd: "",
      beneficiaryAddress: "",
      purchaseLimitPerAccount: "",
      termsAndConditions: "",
      blockedCountries: [],
    },
  });

  // Asset lookup constants
  const sellingAsset =
    assets.allowed.find(
      (a) => a.id.toString() === form.watch("sellingAssetId")
    ) || unknownAsset;
  const purchaseAsset =
    assets.allowed.find(
      (a) => a.id.toString() === form.watch("purchaseAssetId")
    ) || unknownAsset;
  const owner = form.watch("beneficiaryAddress") || activeAddress;
  const sellingAmount = form.watch("amountForSale")
    ? parseFloat(form.watch("amountForSale")) * 10 ** sellingAsset.decimals
    : 0;

  const timeStartValue = form.watch("timeStart");
  const timeStart = timeStartValue
    ? BigInt(+new Date(timeStartValue) / 1000)
    : 0n;
  const timeEndValue = form.watch("timeEnd");
  const timeEnd = timeEndValue ? BigInt(+new Date(timeEndValue) / 1000) : 0n;

  const reimbursible =
    sellingAsset.id === 0 || purchaseAsset.id === 0 ? 0.2 : 0.3;
  const setupFees = N(benchRegistryState?.setupFee ?? 0n) / 1e6 + reimbursible;

  // if selling ALGO, add selling amount + setup fee
  const algoTxnAmount =
    sellingAsset.id === 0
      ? setupFees + N(form.watch("amountForSale"))
      : setupFees;

  const reloadState = (logs?: Uint8Array[]) => {
    clearBenchRegistryCache();
    clearAllBenchCache();
    if (logs?.length) {
      const lastLog = logs[logs.length - 1];
      const benchId = decodeUint64(lastLog.slice(4, 12), "safe");
      navigate(`/bench/${benchId}`);
    }
  };

  const onSubmit = async () => {
    setCurrentStep("review");
  };

  return !activeAddress ? (
    <div className="container flex flex-col gap-4 mx-auto max-w-2xl">
      <BenchHero showBenchLogo={true} />

      <div className="mt-12 flex flex-col md:flex-row justify-center flex-nowrap md:flex-wrap items-center gap-4">
        <ConnectButton
          connectLabel="Connect to create"
          onClick={() => navigate("/bench/create")}
          variant={"outline"}
        />
      </div>
    </div>
  ) : (
    <div className="container flex flex-col gap-4 mx-auto max-w-2xl">
      {/* Header */}
      <div className="flex items-center justify-center gap-4 mb-6 relative">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/bench")}
          className="p-2 absolute left-0"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex flex-col items-center text-xs">
          <div className="text-primary/70 text-primary font-normal text-[1.6rem] animate-bounce">
            ¥
          </div>
          <BenchIcon className="text-primary/70" />
        </div>
      </div>

      {currentStep === "form" ? (
        /* Form Card */
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Create Bench Sale Contract
            </CardTitle>
            <CardDescription>
              Set up a new bench sale contract to sell assets at a constant
              price
            </CardDescription>
          </CardHeader>
          <WithErrorAndLoading error={error} loading={loading}>
            <CardContent>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-6"
                >
                  {/* Asset Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium flex items-center gap-2">
                      <Coins className="h-4 w-4" />
                      Asset Configuration
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="sellingAssetId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Asset for Sale</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select asset to sell" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {assets.allowed.map((asset) => (
                                  <SelectAssetItem
                                    key={asset.id}
                                    asset={asset}
                                  />
                                ))}
                                {assets.unknown.map((asset) => (
                                  <SelectAssetItem
                                    key={asset.id}
                                    disabled={true}
                                    asset={asset}
                                  />
                                ))}
                                {assets.banned.map((asset) => (
                                  <SelectAssetItem
                                    key={asset.id}
                                    disabled={true}
                                    asset={asset}
                                  />
                                ))}
                              </SelectContent>
                            </Select>
                            <FormDescription>
                              The asset you want to sell
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="purchaseAssetId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Purchase Asset</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select asset for purchase" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {assets.allowed.map((asset) => (
                                  <SelectAssetItem
                                    key={asset.id}
                                    asset={asset}
                                  />
                                ))}
                                {assets.unknown.map((asset) => (
                                  <SelectAssetItem
                                    key={asset.id}
                                    disabled={true}
                                    asset={asset}
                                  />
                                ))}
                                {assets.banned.map((asset) => (
                                  <SelectAssetItem
                                    key={asset.id}
                                    disabled={true}
                                    asset={asset}
                                  />
                                ))}
                              </SelectContent>
                            </Select>
                            <FormDescription>
                              The asset buyers will use to purchase
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Asset Relationship Explanation */}
                    {form.watch("sellingAssetId") &&
                      form.watch("purchaseAssetId") && (
                        <Card className="bg-muted/50">
                          <CardContent className="py-4">
                            <div className="text-sm text-center text-muted-foreground">
                              {form.watch("sellingAssetId") ===
                              form.watch("purchaseAssetId") ? (
                                <>
                                  <span className="text-destructive font-medium">
                                    Selling asset and purchase asset can not be
                                    the same.
                                  </span>
                                </>
                              ) : (
                                <>
                                  You are selling{" "}
                                  <span className="font-medium text-foreground">
                                    {form.watch("amountForSale")
                                      ? `${toLocaleString(
                                          Number(form.watch("amountForSale"))
                                        )} `
                                      : ""}
                                    {sellingAsset.unitName}
                                  </span>
                                  . Users will purchase it using{" "}
                                  <span className="font-medium text-foreground">
                                    {purchaseAsset.unitName}
                                  </span>
                                  .{" "}
                                </>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      )}

                    <FormField
                      control={form.control}
                      name="amountForSale"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Amount for Sale</FormLabel>
                          <FormControl>
                            <div className="flex items-center gap-2">
                              <Input
                                placeholder="e.g., 1000"
                                type="number"
                                step="1"
                                {...field}
                              />
                              {form.watch("sellingAssetId") && (
                                <span className="text-sm text-muted-foreground whitespace-nowrap">
                                  {sellingAsset.unitName}
                                </span>
                              )}
                            </div>
                          </FormControl>
                          <FormDescription>
                            Total amount of the selling asset to put up for sale
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="assetPrice"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Asset Price</FormLabel>
                          <FormControl>
                            <div className="flex items-center gap-2">
                              {form.watch("sellingAssetId") && (
                                <span className="text-sm text-muted-foreground whitespace-nowrap">
                                  1 {sellingAsset.unitName} =
                                </span>
                              )}
                              <Input
                                placeholder="e.g., 0.001"
                                type="number"
                                step="0.000001"
                                {...field}
                              />
                              {form.watch("purchaseAssetId") && (
                                <span className="text-sm text-muted-foreground whitespace-nowrap">
                                  {purchaseAsset.unitName}
                                </span>
                              )}
                            </div>
                          </FormControl>
                          <FormDescription>
                            Price per unit of the selling asset
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <Separator />

                  {/* Advanced Options */}
                  <Collapsible
                    open={isAdvancedOpen}
                    onOpenChange={setIsAdvancedOpen}
                    className="space-y-4"
                  >
                    <CollapsibleTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full justify-between"
                      >
                        <span className="flex items-center gap-2">
                          <ChevronDown
                            className={`h-4 w-4 transition-transform ${
                              isAdvancedOpen ? "rotate-180" : ""
                            }`}
                          />
                          Advanced Options
                        </span>
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="space-y-6">
                      {/* Terms and Conditions Configuration */}
                      <TermsAndConditionsField
                        control={form.control}
                        name="termsAndConditions"
                        edit={true}
                      />

                      <Separator />

                      {/* Purchase Limit Configuration */}
                      <div className="space-y-4">
                        <h3 className="text-lg font-medium flex items-center gap-2">
                          <User className="h-4 w-4" />
                          Purchase Limit Configuration (Optional)
                        </h3>

                        <FormField
                          control={form.control}
                          name="purchaseLimitPerAccount"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Purchase Limit Per Account</FormLabel>
                              <FormControl>
                                <div className="flex items-center gap-2">
                                  <Input
                                    placeholder="e.g., 100"
                                    type="number"
                                    step="1"
                                    {...field}
                                  />
                                  {form.watch("purchaseAssetId") && (
                                    <span className="text-sm text-muted-foreground whitespace-nowrap">
                                      {purchaseAsset.unitName}
                                    </span>
                                  )}
                                </div>
                              </FormControl>
                              <FormDescription>
                                Maximum amount that can be purchased per
                                account. Leave empty to disable.
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <Separator />

                      {/* Time Configuration */}
                      <div className="space-y-4">
                        <h3 className="text-lg font-medium flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          Time Configuration (Optional)
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name="timeStart"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Start Time</FormLabel>
                                <FormControl>
                                  <Input type="datetime-local" {...field} />
                                </FormControl>
                                <FormDescription>
                                  When the bench should start accepting
                                  purchases
                                </FormDescription>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="timeEnd"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>End Time</FormLabel>
                                <FormControl>
                                  <Input type="datetime-local" {...field} />
                                </FormControl>
                                <FormDescription>
                                  When the bench should stop accepting purchases
                                </FormDescription>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                        <p className="text-sm text-muted-foreground">
                          You can set either start time, end time, or both.
                          Leave empty for no time restrictions.
                        </p>
                      </div>

                      <Separator />

                      {/* Beneficiary Configuration */}
                      <div className="space-y-4">
                        <h3 className="text-lg font-medium flex items-center gap-2">
                          <User className="h-4 w-4" />
                          Beneficiary Configuration (Optional)
                        </h3>

                        <FormField
                          control={form.control}
                          name="beneficiaryAddress"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Beneficiary Address</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="e.g., ABCDEFGHIJKLMNOPQRSTUVWXYZ234567ABCDEFGHIJKLMNOPQRSTUVWXYZ234567"
                                  {...field}
                                />
                              </FormControl>
                              <FormDescription>
                                Algorand address that will receive the proceeds
                                from the bench sale. Leave empty to use your own
                                address.
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <Separator />

                      {/* Geo-blocking Configuration */}
                      <BlockedCountriesField
                        control={form.control}
                        name="blockedCountries"
                        showHeader={true}
                      />
                    </CollapsibleContent>
                  </Collapsible>

                  {/* Submit Button */}
                  <div className="flex justify-end gap-4 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate("/bench")}
                    >
                      Cancel
                    </Button>
                    <Button type="submit">
                      <ListChecks /> Next: Review
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </WithErrorAndLoading>
        </Card>
      ) : (
        /* Review Panel */
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Review Bench Configuration
            </CardTitle>
            <CardDescription>
              Please review your bench configuration before proceeding
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Asset Configuration Review */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium flex items-center gap-2">
                  <Coins className="h-4 w-4" />
                  Asset Configuration
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      Asset for Sale
                    </Label>
                    <div className="text-sm text-muted-foreground">
                      {sellingAsset.unitName}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      Asset for Purchase
                    </Label>
                    <div className="text-sm text-muted-foreground">
                      {purchaseAsset.unitName}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      Amount for Sale
                    </Label>
                    <div className="text-sm text-muted-foreground">
                      {form.watch("amountForSale")
                        ? `${toLocaleString(
                            Number(form.watch("amountForSale"))
                          )} ${sellingAsset.unitName}`
                        : "Not specified"}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Asset Price</Label>
                    <div className="text-sm text-muted-foreground">
                      {form.watch("assetPrice")
                        ? `1 ${sellingAsset.unitName} = ${Number(
                            form.watch("assetPrice")
                          )
                            .toFixed(6)
                            .replace(/\.?0+$/, "")} ${purchaseAsset.unitName}`
                        : "Not specified"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Advanced Options Review */}
              {(form.watch("purchaseLimitPerAccount") ||
                form.watch("timeStart") ||
                form.watch("timeEnd") ||
                form.watch("beneficiaryAddress") ||
                form.watch("termsAndConditions") ||
                (form.watch("blockedCountries") &&
                  form.watch("blockedCountries").length > 0)) && (
                <>
                  <Separator />

                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {form.watch("timeStart") && (
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">
                            Start Time
                          </Label>
                          <div className="text-sm text-muted-foreground">
                            {new Date(
                              form.watch("timeStart")!
                            ).toLocaleString()}
                          </div>
                        </div>
                      )}

                      {form.watch("timeEnd") && (
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">
                            End Time
                          </Label>
                          <div className="text-sm text-muted-foreground">
                            {new Date(form.watch("timeEnd")!).toLocaleString()}
                          </div>
                        </div>
                      )}

                      {form.watch("timeEnd") && (
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">
                            Duration
                          </Label>
                          <div className="text-sm text-muted-foreground">
                            {(() => {
                              const start = form.watch("timeStart")
                                ? new Date(form.watch("timeStart")!)
                                : new Date();
                              const end = new Date(form.watch("timeEnd")!);
                              const diffMs = end.getTime() - start.getTime();

                              if (diffMs <= 0) return "Invalid duration";
                              return showTimeDiffPartial(diffMs, 3, false);
                            })()}
                          </div>
                        </div>
                      )}

                      {form.watch("purchaseLimitPerAccount") && (
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">
                            Purchase Limit Per Account
                          </Label>
                          <div className="text-sm text-muted-foreground">
                            {toLocaleString(
                              Number(form.watch("purchaseLimitPerAccount"))
                            )}{" "}
                            {purchaseAsset.unitName}
                            {form.watch("assetPrice") && (
                              <span className="text-xs text-muted-foreground/70">
                                {" "}
                                (
                                {toLocaleString(
                                  Number(
                                    form.watch("purchaseLimitPerAccount")
                                  ) / Number(form.watch("assetPrice"))
                                )}{" "}
                                {sellingAsset.unitName})
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {form.watch("beneficiaryAddress") && (
                        <div
                          className={`space-y-2 ${
                            form.watch("beneficiaryAddress") === activeAddress
                              ? ""
                              : "text-amber-600 dark:text-amber-400"
                          }`}
                        >
                          <Label className="text-sm font-medium">
                            Beneficiary Address
                          </Label>
                          <div
                            className={`text-sm ${
                              form.watch("beneficiaryAddress") === activeAddress
                                ? "text-muted-foreground"
                                : "opacity-70"
                            }`}
                            title={form.watch("beneficiaryAddress")}
                          >
                            <AccountNoLink
                              address={form.watch("beneficiaryAddress")!}
                            />
                          </div>
                        </div>
                      )}

                      {form.watch("termsAndConditions") && (
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">
                            Terms and Conditions URL
                          </Label>
                          <div className="text-sm text-muted-foreground">
                            <a
                              href={form.watch("termsAndConditions")}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline break-all"
                              title={form.watch("termsAndConditions")}
                            >
                              {form.watch("termsAndConditions")}
                            </a>
                          </div>
                        </div>
                      )}

                      {form.watch("blockedCountries") &&
                        form.watch("blockedCountries").length > 0 && (
                          <div className="space-y-2">
                            <Label className="text-sm font-medium">
                              Restricted Countries
                            </Label>
                            <div className="flex flex-wrap gap-1">
                              {form.watch("blockedCountries").map((code) => {
                                const country = countries.find(
                                  (c: CountryOption) => c.code === code
                                );
                                return (
                                  <Badge
                                    key={code}
                                    variant="destructive"
                                    className="text-xs"
                                  >
                                    {country?.name || code}
                                  </Badge>
                                );
                              })}
                            </div>
                          </div>
                        )}
                    </div>

                    {/* Warning for Geo-blocking */}
                    {form.watch("blockedCountries") &&
                      form.watch("blockedCountries").length > 0 && (
                        <div className="col-span-1 md:col-span-2">
                          <Card className="border-destructive/20 bg-destructive/10">
                            <CardContent className="p-4">
                              <div className="flex items-start gap-3">
                                <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Label className="text-sm font-bold text-destructive">
                                      Country Blocking Notice
                                    </Label>
                                  </div>
                                  <p className="text-sm text-foreground">
                                    This feature relies on IP address
                                    geolocation on the browser side. It is only
                                    enforced on the myth.finance website.
                                  </p>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        </div>
                      )}
                  </div>
                </>
              )}

              <Separator />

              {/* Revenue Calculation */}
              {form.watch("amountForSale") && form.watch("assetPrice") && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium flex items-center gap-2">
                    <Coins className="h-4 w-4" />
                    Fees & Revenue
                  </h3>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center text-amber-600 dark:text-amber-400">
                      <Label className="text-sm font-medium">Setup cost</Label>
                      <div className="text-sm font-medium">
                        -{setupFees} ALGO
                      </div>
                    </div>
                    <div className="flex justify-between items-center text-muted-foreground">
                      <Label className="text-sm font-medium">
                        Reimbursed at end
                      </Label>
                      <div className="text-sm font-medium">
                        {reimbursible} ALGO
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center text-muted-foreground">
                      <Label className="text-sm font-medium">
                        Total Revenue
                      </Label>
                      <div className="text-sm font-medium">
                        {toLocaleString(
                          Number(form.watch("amountForSale")) *
                            Number(form.watch("assetPrice"))
                        )}{" "}
                        {purchaseAsset.unitName}
                      </div>
                    </div>

                    <div className="flex justify-between items-center text-amber-600 dark:text-amber-400">
                      <Label className="text-sm">
                        Platform Fee (
                        {(benchRegistryState?.defaultFeeBps ?? 0) / 100}%)
                      </Label>
                      <div className="text-sm">
                        -
                        {toLocaleString(
                          Number(form.watch("amountForSale")) *
                            Number(form.watch("assetPrice")) *
                            ((benchRegistryState?.defaultFeeBps ?? 0) / 10000),
                          purchaseAsset.decimals
                        )}{" "}
                        {purchaseAsset.unitName}
                      </div>
                    </div>

                    <div className="flex justify-between items-center font-bold">
                      <Label className="text-sm">Net Revenue</Label>
                      <div className="text-sm">
                        {toLocaleString(
                          Number(form.watch("amountForSale")) *
                            Number(form.watch("assetPrice")) *
                            (1 -
                              (benchRegistryState?.defaultFeeBps ?? 0) / 10000),
                          purchaseAsset.decimals
                        )}{" "}
                        {purchaseAsset.unitName}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Warning for Beneficiary Address */}
              {form.watch("beneficiaryAddress") &&
                form.watch("beneficiaryAddress") !== activeAddress && (
                  <div className="space-y-4">
                    <div className="p-4 border border-destructive/20 bg-destructive/10 rounded-lg">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                          <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-sm font-bold text-destructive">
                            Important Warning
                          </h4>
                          <div className="mt-1 text-sm">
                            <p>
                              You have set a beneficiary address for this bench
                              sale. Please note:
                            </p>
                            <ul className="mt-2 list-disc list-inside space-y-1">
                              <li>
                                You will <strong>not have access</strong> to
                                manage this bench or control the revenue
                              </li>
                              <li>
                                The beneficiary address will receive{" "}
                                <strong>all proceeds</strong> from the sale
                              </li>
                              <li>
                                This process is <strong>irreversible</strong>{" "}
                                once the bench is created
                              </li>
                              <li>
                                Make sure the beneficiary address is correct
                                before proceeding
                              </li>
                            </ul>

                            <div className="mt-4 flex items-start gap-2">
                              <input
                                type="checkbox"
                                id="acknowledge-warning"
                                checked={acknowledgeWarning}
                                onChange={(e) =>
                                  setAcknowledgeWarning(e.target.checked)
                                }
                                className="mt-1 h-4 w-4 rounded border-gray-300 text-destructive focus:ring-destructive"
                              />
                              <label
                                htmlFor="acknowledge-warning"
                                className="text-sm font-bold text-foreground"
                              >
                                I understand and acknowledge that this action is
                                irreversible
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              {/* Action Buttons */}
              <div className="flex justify-end gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCurrentStep("form")}
                >
                  Back to Edit
                </Button>
                <TransactionDialog
                  type="purchase"
                  trigger={(onClick) => (
                    <ConnectButton
                      onClick={onClick}
                      disabled={
                        form.watch("beneficiaryAddress") &&
                        form.watch("beneficiaryAddress") !== activeAddress
                          ? !acknowledgeWarning
                          : false
                      }
                    >
                      <WrenchIcon size={18} className="mr-1.5" />
                      <span>Create Bench</span>
                    </ConnectButton>
                  )}
                  transactionBuilder={() => {
                    const {
                      sellingAssetId,
                      purchaseAssetId,
                      assetPrice,
                      purchaseLimitPerAccount = 0,
                      termsAndConditions,
                      blockedCountries,
                    } = form.getValues();
                    const purchaseDecimals = 10 ** purchaseAsset.decimals
                    const meta = blockedCountries.length || termsAndConditions ? {
                    ...blockedCountries.length ? { bc: blockedCountries } : null,
                    ...termsAndConditions ? { tc: termsAndConditions } : null,
                    } : undefined
                    return makeCreateBenchTransactions({
                      deployer: activeAddress,
                      owner: owner!,
                      sellingAsset: BigInt(sellingAssetId),
                      sellingAmount: BigInt(sellingAmount),
                      purchaseAsset: BigInt(purchaseAssetId),
                      purchaseLimit: purchaseLimitPerAccount ? BigInt(parseFloat(purchaseLimitPerAccount) * purchaseDecimals) : 0n,
                      timeStart,
                      timeEnd,
                      purchasePricePer_100m:
                        BigInt(
                          parseFloat(assetPrice) *
                            purchaseDecimals *
                            10 ** 8
                        ) / BigInt(purchaseDecimals),
                      meta,
                    });
                  }}
                  title={`Create ${sellingAsset.unitName}<>${purchaseAsset.unitName} bench`}
                  onClose={() => reloadState()}
                  onSuccess={(logs?: Uint8Array[]) => reloadState(logs)}
                >
                  {transactionInner(
                    "operation",
                    <>
                      You are going to{" "}
                      <strong>
                        create {aOrAn(sellingAsset.unitName)}{" "}
                        {sellingAsset.unitName}-{purchaseAsset.unitName} bench
                      </strong>
                    </>,
                    <>
                      <div className="flex justify-between gap-2 mb-2">
                        <div className="flex items-center">
                          <UserRound size={18} className="mr-1" /> Account
                        </div>{" "}
                        <span>{shorten(activeAddress!)}</span>
                      </div>

                      <div className="flex justify-between gap-2 text-foreground mb-2">
                        <div className="flex items-center">
                          <ChevronRight size={18} className="mr-1" />{" "}
                          {sellingAsset.id === 0 ? (
                            <>Deposit & pay fee</>
                          ) : (
                            <>Pay fee</>
                          )}
                        </div>
                        <span className="text-right">{algoTxnAmount} ALGO</span>
                      </div>

                      {sellingAsset.id === 0 ? null : (
                        <>
                          <div className="flex justify-between gap-2 mb-2 text-foreground">
                            <div className="flex items-center">
                              <ChevronRight size={18} className="mr-1" /> Bench
                              Registry: Opt in {sellingAsset.unitName}
                            </div>
                            <span>App {N(benchRegistryAppId)}</span>
                          </div>

                          <div className="flex justify-between gap-2 mb-2 text-foreground">
                            <div className="flex items-center">
                              <ChevronRight size={18} className="mr-1" />{" "}
                              Deposit
                            </div>
                            <span className="text-right">
                              {toLocaleString(
                                Number(form.watch("amountForSale")),
                                2
                              )}{" "}
                              {sellingAsset.unitName}
                            </span>
                          </div>
                        </>
                      )}

                      <div className="flex justify-between gap-2 text-foreground">
                        <div className="flex items-center">
                          <ChevronRight size={18} className="mr-1" /> Bench
                          Registry: Create bench
                        </div>
                        <span>App {N(benchRegistryAppId)}</span>
                      </div>
                    </>
                  )}
                </TransactionDialog>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
